import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Audience } from 'app/audience/components/audience.model';
import {
  AudiencePickerReachService,
  PickerReachView,
  pickerReachView,
} from 'app/data-platform/audiences/audience-picker-reach.service';

interface AudienceItemView {
  audience: Audience;
  isDisabled: boolean;
  reach: PickerReachView;
}

@Component({
  selector: 'app-audience-selector',
  templateUrl: './audience-selector.component.html',
  styleUrls: ['./audience-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceSelectorComponent implements AfterViewChecked {
  @Input() set items(value: Audience[]) {
    this._items.set(value ?? []);
  }

  @Input() set value(val: string | null | undefined) {
    this._selectedId.set(val ?? null);
  }

  @Input({ required: true }) featurePage!: string;
  @Input() placeholder = '';
  @Input() showCreateNew = false;
  @Input() disableCreateNew = false;
  @Input() createNewTooltip = '';
  @Input() excludeNames: string[] = [];
  // When set, audiences that carry session conditions can't be selected (e.g. native-app campaigns).
  @Input() set disableItemsWithSessionConditions(value: boolean) {
    this._disableItemsWithSessionConditions.set(value);
  }
  @Input() sessionConditionTooltip = '';

  // Internal trigger mode extras
  @Input() editEnabled = false;
  @Input() set showReachInTrigger(value: boolean) {
    this.isReachInTrigger.set(value);
  }

  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
  }

  @Output() selectionChange = new EventEmitter<Audience>();
  @Output() createNewClicked = new EventEmitter<void>();
  @Output() editClicked = new EventEmitter<void>();

  @ViewChild('searchInput') private readonly searchInputRef: ElementRef<HTMLInputElement>;

  private readonly pickerReach = inject(AudiencePickerReachService);

  protected isDisabled = false;
  protected isOpen = false;
  private shouldFocusSearch = false;

  private readonly _items = signal<Audience[]>([]);
  private readonly _selectedId = signal<string | null>(null);
  private readonly _disableItemsWithSessionConditions = signal(false);
  protected readonly isReachInTrigger = signal(false);
  protected readonly searchQuery = signal('');
  protected readonly sortAsc = signal(false);

  protected readonly selectedItem = computed(() => {
    const id = this._selectedId();
    if (!id) return null;
    return this._items().find((audience) => audience.id === id) ?? null;
  });

  protected readonly reachItem = computed(
    () => this.selectedItem() ?? this._items().find((audience) => !audience.id) ?? null,
  );

  protected readonly triggerReach = computed(() => {
    const audience = this.reachItem();
    return audience ? pickerReachView(this.pickerReach.reachOf(audience)) : null;
  });

  protected readonly filteredItems = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const direction = this.sortAsc() ? 1 : -1;

    let list = this._items();
    if (this.excludeNames.length) {
      list = list.filter((audience) => !this.excludeNames.includes(audience.name ?? ''));
    }
    if (query) {
      list = list.filter((audience) => audience.name?.toLowerCase().includes(query));
    }

    const getTime = (audience: Audience) => {
      const dateStr = audience.updatedTime ?? audience.createdTime;
      return dateStr ? new Date(dateStr).getTime() : 0;
    };

    return [...list].sort((left, right) => (getTime(left) - getTime(right)) * direction);
  });

  protected readonly itemViews = computed<AudienceItemView[]>(() =>
    this.filteredItems().map((audience) => ({
      audience,
      isDisabled: this.isItemDisabled(audience),
      reach: pickerReachView(this.pickerReach.reachOf(audience)),
    })),
  );

  protected readonly sortIcon = computed(() => (this.sortAsc() ? 'arrow_upward' : 'arrow_downward'));

  constructor() {
    effect(() => {
      const audience = this.reachItem();
      if (audience && this.isReachInTrigger()) {
        untracked(() => this.pickerReach.request([audience], this.featurePage));
      }
    });
  }

  protected toggleDropdown(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchQuery.set('');
      this.shouldFocusSearch = true;
      this.pickerReach.request(this.filteredItems(), this.featurePage);
    }
  }

  protected closeDropdown(): void {
    this.isOpen = false;
  }

  private isItemDisabled(audience: Audience): boolean {
    return this._disableItemsWithSessionConditions() && (audience.sessionConditions?.length ?? 0) > 0;
  }

  protected selectItem(audience: Audience): void {
    if (this.isItemDisabled(audience)) {
      return;
    }
    this._selectedId.set(audience.id ?? null);
    this.isOpen = false;
    this.selectionChange.emit(audience);
  }

  protected toggleSort(): void {
    this.sortAsc.update((current) => !current);
  }

  protected onCreateNew(event: Event): void {
    event.stopPropagation();
    this.isOpen = false;
    this.createNewClicked.emit();
  }

  protected onEdit(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.editClicked.emit();
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusSearch && this.searchInputRef) {
      this.searchInputRef.nativeElement.focus();
      this.shouldFocusSearch = false;
    }
  }
}
